<script lang="ts">
	//Modules
	import AppLayout from "../layouts/AppLayout.svelte";
	import Form from "../layouts/Form.svelte";
	import ForgotTabs from "../components/ForgotTabs.svelte";
	import NewResultLayout from "../layouts/NewResultLayout.svelte";

	//Split
	import { onMount } from "svelte";
	import { navigate } from "svelte-routing";
	import { api, t } from "@hitit/hub/src/stores";
	import {
		InputFrame,
		DefaultSelect,
		DefaultTextInput,
		DefaultButton,
		OutlineButton,
		DefaultDone,
		TextButton,
	} from "@hitit/new_ui/src";
	import {
		getCountries,
		getCountryCallingCode,
		parsePhoneNumber,
	} from "libphonenumber-js";

	//Constants
	const countryNames: Record<string, string> = $t("global.countries", {
		returnObjects: true,
	});

	//variables
	let step: 0 | 1 | 2 = 0;

	let foundUsername = "";
	let authenticationNo = "";
	let authenticationNoInputValue = "";

	let phonenumberCode = "82";
	let phoneNumber = "";

	let authenticationNoFrame: HTMLDivElement;

	let loading = false;

	let codeError = false;
	let phoneError = false;
	let errorMessage = "";
	let copyTimeout: any;

	let copyStatus: "normal" | "error" | "success" = "normal";

	$: countries = getCountries().map((v) => ({
		label: `${countryNames[v.toLowerCase()]} +${getCountryCallingCode(v)}`,
		value: getCountryCallingCode(v) as string,
		display: `+${getCountryCallingCode(v)}`,
	}));

	$: buttondisabled =
		step === 0 && phoneNumber === ""
			? true
			: step === 1 && authenticationNoInputValue === "";

	//do
	onMount(() => {
		authenticationNoFrame.addEventListener("transitionend", () => {
			if (step === 1) {
				//transition end bug
				setTimeout(() => {
					authenticationNoFrame.style.overflowY = "initial";
				}, 500);
			} else {
				authenticationNoFrame.style.overflowY = "hidden";
			}
		});
	});

	//functions
	async function resendHandler() {
		try {
			const parsedPhone = parsePhoneNumber(
				`+${phonenumberCode} ${phoneNumber}`
			);
			const nationalNumber = parsedPhone?.nationalNumber;

			if (!(parsedPhone && parsedPhone.isValid())) {
				phoneError = true;
				throw new Error($t("sso.error.phoneNumberisNotCorrectFormat"));
			}
			authenticationNo = await sendAuthenticationNumb(
				phonenumberCode,
				nationalNumber
			);
		} catch (e) {
			const __error = e as Error;
			errorMessage = __error.message;
		}
	}

	async function sendAuthenticationNumb(
		phonenumberCode: string,
		nationalNumber: string
	) {
		try {
			loading = true;
			const { data } = await $api.requestMobileCode(
				nationalNumber,
				phonenumberCode
			);
			return data.response;
		} catch (e) {
			throw new Error($t("sso.error.unknownError"));
		} finally {
			loading = false;
		}
	}

	async function getEmail(countryCode: string, phoneNumber: string) {
		try {
			loading = true;
			const { data } = await $api.getUsernameByPhone(countryCode, phoneNumber);
			return data.response as string;
		} catch {
			throw new Error($t("sso.error.Email_not_found"));
		}
	}

	async function submitHandler(e: Event) {
		try {
			e.preventDefault();
			loading = true;
			if (phoneError || codeError) return;

			const parsedPhone = parsePhoneNumber(
				`+${phonenumberCode} ${phoneNumber}`
			);
			const nationalNumber = parsedPhone?.nationalNumber;

			if (!(parsedPhone && parsedPhone.isValid())) {
				phoneError = true;
				throw new Error($t("sso.error.phoneNumberisNotCorrectFormat"));
			}

			if (step === 0) {
				authenticationNo = await sendAuthenticationNumb(
					phonenumberCode,
					nationalNumber
				);
				step = 1;
			} else if (step === 1) {
				if (authenticationNoInputValue !== authenticationNo) {
					codeError = true;
					throw new Error($t("sso.error.verificationNumberIsIncorrect"));
				}

				foundUsername = await getEmail(phonenumberCode, nationalNumber);
				step = 2;
			}
			errorMessage = "";
		} catch (e) {
			const __error = e as Error;
			errorMessage = __error.message;
		} finally {
			loading = false;
		}
	}

	function copyEmail() {
		try {
			if (copyTimeout) clearTimeout(copyTimeout);
			navigator.clipboard.writeText(foundUsername);
			copyStatus = "success";
		} catch (e) {
			copyStatus = "error";
		} finally {
			copyTimeout = setTimeout(() => (copyStatus = "normal"), 2000);
		}
	}
	function phoneNumberHandler(isClear = false) {
		authenticationNoInputValue = "";
		authenticationNo = "";
		step = 0;
		phoneError = false;
		codeError = false;
		errorMessage = "";
		if (isClear) phoneNumber = "";
	}

	function authenticationNoHandler(isClear = false) {
		phoneError = false;
		codeError = false;
		errorMessage = "";
		if (isClear) authenticationNoInputValue = "";
	}

	function keyDownEventHandler(e: KeyboardEvent) {
		if (e.key === "-") {
			e.preventDefault();
		}
	}

</script>

<AppLayout>
	{#if foundUsername === ''}
		<Form on:submit={(e) => submitHandler(e)}>
			<ForgotTabs active={'username'} />
			<InputFrame
				-forgotId={true}
				defaultClassName={'forgotIdInputFrame'}
				gap={authenticationNo !== '' ? 19.5 : 0}
				direction="column">
				<InputFrame>
					<div class="side-box">
						<DefaultSelect bind:value={phonenumberCode} items={countries} />
					</div>
					<div class="input-box">
						<DefaultTextInput
							id="phone"
							on:input={() => phoneNumberHandler()}
							on:keydown={(e) => keyDownEventHandler(e)}
							bind:value={phoneNumber}
							disabled={loading}
							error={phoneError}
							placeholder={$t('sso.phone')}
							clearButtonDisplay={phoneNumber.length > 0 && !loading}
							clearCallback={() => phoneNumberHandler(true)} />
					</div>
				</InputFrame>
				<div
					bind:this={authenticationNoFrame}
					class="authentication-frame"
					class:hide={authenticationNo === ''}>
					<InputFrame>
						<div class="input-box">
							<DefaultTextInput
								id="authentication"
								on:input={() => authenticationNoHandler()}
								bind:value={authenticationNoInputValue}
								placeholder={$t('sso.code')}
								error={codeError}
								clearButtonDisplay={phoneNumber.length > 0}
								clearCallback={() => authenticationNoHandler(true)} />
						</div>
						<div class="side-box resend">
							<OutlineButton
								let:hovering={resendHovering}
								on:click={resendHandler}
								disabled={step !== 1 || loading}>
								<span class:hover={resendHovering} class="white">
									{$t('sso.resend')}
								</span>
							</OutlineButton>
						</div>
					</InputFrame>
				</div>
			</InputFrame>
			<p class="error">{errorMessage ? errorMessage : ''}</p>
			<DefaultButton isLoading={loading} disabled={buttondisabled}>
				<span class="sendbutton" class:disabled={buttondisabled}>
					{step == 0 ? $t('sso.forgot_username.send_authentication_no') : $t('sso.forgot_username.confirm')}
				</span>
			</DefaultButton>
		</Form>
	{:else}
		<NewResultLayout text={foundUsername}>
			<div class="done-box" slot="result-icon">
				<DefaultDone />
			</div>
			<div class="result-buttons">
				{#if copyStatus === 'normal' || copyStatus === 'success'}
					<TextButton
						text={copyStatus === 'normal' ? $t('sso.forgot_username.email_copy') : $t('sso.forgot_username.copy_success')}
						on:click={copyEmail} />
				{:else}
					<span class="copyStatus" class:error={copyStatus === 'error'}>
						{$t('sso.forgot_username.copy_failed')}
					</span>
				{/if}
				<empty />
				<InputFrame gap={20}>
					<DefaultButton
						defaultClassname={'find-password'}
						on:click={() => navigate('/forgotPassword')}>
						<span> {$t('sso.forgot_password.forgot_password')} </span>
					</DefaultButton>
					<DefaultButton on:click={() => navigate('/')}>
						<span class="white">LOGIN</span>
					</DefaultButton>
				</InputFrame>
			</div>
		</NewResultLayout>
	{/if}
</AppLayout>

<style lang="scss">
	@import "../variables.scss";
	:global(.forgotIdInputFrame) {
		transition: all 0.5s;
	}
	:global(.result-buttons .find-password) {
		background-color: #bcbfc3;
		color: #46474b;
	}
	:global(.result-buttons .find-password:not(:disabled):hover) {
		background-color: #acb1b6;
	}

	.authentication-frame {
		height: 40px;
		transition: height 0.5s;
		overflow-y: hidden;
		&.hide {
			height: 0px;
			overflow-y: hidden !important;
		}
	}
	.side-box {
		flex-basis: 100px;
		&.resend {
			flex-basis: 90px;
		}
	}
	.input-box {
		flex-grow: 1;
	}

	.done-box {
		width: 121px;
		height: 115.2px;
	}
	.result-buttons {
		display: flex;
		margin-top: 20px;
		flex-direction: column;
		align-items: center;
		max-width: 400px;
		width: 100%;
		flex-grow: 1;
		max-height: 129px;
	}

	empty {
		display: block;
		min-height: 15px;
		flex-grow: 1;
		max-height: 70px;
	}

	span.white {
		color: white;
	}

	span {
		transition: color 0.15s;
		&.sendbutton {
			color: white;
			&.disabled {
				color: var(--default-disabled-btn-2);
			}
		}
	}
	p.error {
		box-sizing: border-box;
		padding: 10px;
		height: 50px;
		color: var(--error-color-1);
	}
	.copyStatus.error {
		display: block;
		color: var(--error-color-1);
		padding: 2px 6px 3px 6px;
	}

	@include DesktopSize {
		span {
			&.hover {
				color: var(--signature-blue);
			}
		}
	}

</style>
